import { useEffect } from "react";
import PHFHero from "../shared/PHFHero";
import PHFIntro from "../shared/PHFIntro";
import PHFFeatures from "../shared/PHFFeatures";
import PHFHowItWorks from "../shared/PHFHowItWorks";
import PHFCTA from "../shared/PHFCTA";

const sponsorsHero = {
  sm: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-sponsor-hero-480.webp",
  md: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-sponsor-hero-1280.webp",
  lg: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-sponsor-hero-1980.webp",
  src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-sponsor-hero-1280.webp",
};

const SponsorsPage = () => {
  useEffect(() => {
    document.title = "Become a Program Sponsor – Battle Buddies | Sponsor a Pet";
  }, []);

  return (
    <main>
      <PHFHero
        image={sponsorsHero}
        title="Fund the moment a veteran & a companion become Battle Buddies"
        subtext="Your support removes adoption barriers for veterans while helping rescue animals find stable homes."
        buttonText="Become a program sponsor"
        buttonUrl="https://purpleheartfoundation.org/battle-buddies/"
        disclaimer="Companion animals / emotional support animals. Not service animals."
      />
      <section aria-label="Why sponsor">
        <PHFIntro
          title="The barrier isn't care—it's cost"
          body="Many veterans are ready for the responsibility & stability a companion animal provides, but upfront adoption fees & initial medical costs can stop the process before it begins."
          body2="Battle Buddies removes that first financial hurdle — allowing readiness, not cost, to determine placement."
        />
      </section>
      <section aria-label="What your support covers">
        <PHFFeatures
          image="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-dog2.png"
          title="What your support covers"
          subtext="This program is designed to remove common barriers to adoption while prioritizing safe, thoughtful placements."
          items={[
            "Adoption fees",
            "Spay/neuter",
            "Initial wellness exam",
            "Core vaccines & testing",
            "Microchip",
            "Starter supplies (when available through partner rescues)",
          ]}
          disclaimer="All funds are applied directly to verified, upfront adoption-related expenses."
        />
      </section>
      <section aria-label="How it works">
        <PHFHowItWorks
          bgColor="bg-blue-light1/30"
          titleColor="text-blue-navy"
          title="A simple, accountable model"
          steps={[
            {
              title: "Veteran eligibility confirmed",
              description: "Purple Heart Foundation verifies eligibility to support safe, sustainable placements.",
            },
            {
              title: "Rescue identifies a suitable match",
              description: "Partner rescues prioritize temperament, lifestyle fit, & long-term success.",
            },
            {
              title: "Verified costs are funded",
              description: "Approved upfront adoption & medical expenses are covered, with tracking in place.",
            },
            {
              title: "Adoption completed & impact reported",
              description: "The veteran & companion are placed, & donors receive confirmation at the program level.",
            },
          ]}
        />
      </section>
      <section aria-label="A shared win">
        <div className="bg-white py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="!text-[36px] font-bold text-blue-navy font-poppins text-left leading-tight mb-4">
              A shared win
            </h2>
            <p className="!text-[18px] text-blue-navy text-left mb-12 max-w-[671px]">
              Designed to benefit veterans, nonprofit rescue partners, &amp; the supporters who make each placement possible.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden shadow-sm">
              {/* Sponsor benefits */}
              <div className="bg-blue px-12 py-12">
                <h3 className="text-blue-navy font-bold text-[22px] font-poppins mb-8">
                  Sponsor benefits
                </h3>
                <ul className="space-y-5">
                  {[
                    "Reduces financial strain tied to upfront medical & adoption costs",
                    "Supports responsible placements without rushing or compromise",
                    "Increases adoption success by prioritizing fit & long-term stability",
                    "Strengthens trust with donors through transparent, program-level reporting",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-navy flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-white text-[15px] leading-relaxed text-left">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Veteran benefits */}
              <div className="bg-blue-navy px-12 py-12">
                <h3 className="text-blue font-bold text-[22px] font-poppins mb-8">
                  Veteran benefits
                </h3>
                <ul className="space-y-5">
                  {[
                    "Removes financial barriers to adoption",
                    "Supports emotional grounding, routine, & daily responsibility",
                    "Encourages stable, long-term companionship — not temporary placements",
                    "Early post-placement check-ins to support a successful transition",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-white text-[15px] leading-relaxed text-left">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex justify-center mt-12">
              <a
                href="https://purpleheartfoundation.org/battle-buddies/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-orange text-white font-semibold text-[16px] px-8 py-3 rounded-full hover:opacity-90 transition-opacity"
              >
                Ready to support a placement?
              </a>
            </div>
          </div>
        </div>
      </section>
      <section aria-label="Built for long-term impact">
        <div className="flex flex-col lg:flex-row min-h-[500px]">
          <div className="lg:w-1/2 bg-blue-light1/30 flex items-center px-12 py-20">
            <div className="max-w-lg">
              <h2 className="!text-[36px] font-bold text-blue-navy font-poppins text-left leading-tight mb-8">
                Built for long-term impact
              </h2>
              <p className="!text-[16px] text-blue-navy text-left leading-relaxed mb-8">
                Battle Buddies is a pilot partnership between <strong>Purple Heart Foundation</strong>, <strong>Sponsor a Pet</strong>, &amp; vetted rescue partners — built to scale responsibly while prioritizing placement quality, transparency, &amp; long-term outcomes.
              </p>
              <p className="!text-[13px] text-blue-navy/60 text-left">
                Companion animals / emotional support animals. Not service animals.
              </p>
            </div>
          </div>
          <div className="lg:w-1/2">
            <img
              src="https://sap-app-prod.s3.us-west-2.amazonaws.com/phf/phf-dog3.png"
              alt="Veteran in wheelchair with companion dog"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>
      <section aria-label="Become a program sponsor">
        <PHFCTA
          title="Help fund a connection that lasts"
          body="Your contribution directly supports verified adoption & medical costs, helping veterans move forward while enabling responsible rescue placements."
          buttonText="Become a program sponsor"
          buttonUrl="https://purpleheartfoundation.org/battle-buddies/"
        />
      </section>
    </main>
  );
};

export default SponsorsPage;
